import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "../../../app/stores/rootStore";

export default observer(function WishlistPage() {
    const { wishlistStore, cartStore, userStore: { user } } = useStore();
    const navigate = useNavigate();

    useEffect(() => {
        // Sync wishlist based on current session status
        wishlistStore.syncWishlist(user?.uid);
    }, [user?.uid, wishlistStore]);

    const handleRemoveFromWishlist = (e: React.MouseEvent, item: any) => {
        e.preventDefault();
        e.stopPropagation();
        // The store logic handles removal if the item already exists
        wishlistStore.toggleWishlist(user?.uid, item);
    };

    return (
        <section className="header1 cid-uATOSlQKYb py-5 bg-light" id="wishlist-section">
            <style>{`
                .hover-shadow-transition {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .hover-shadow-transition:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.1) !important;
                }
                .x-small { font-size: 0.75rem; }
                @media (max-width: 576px) {
                    .mbr-section-title { font-size: 1.8rem; }
                }
            `}</style>

            <div className="container mt-5">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10">
                        {/* Header Section */}
                        <div className="d-flex justify-content-between align-items-end mb-5">
                            <div>
                                <h2 className="mbr-section-title fw-bold display-5 mb-1">My Wishlist</h2>
                                <p className="text-muted mb-0">
                                    {wishlistStore.itemCount} {wishlistStore.itemCount === 1 ? 'item' : 'items'} saved to your list
                                </p>
                            </div>
                            <Link to="/catalog" className="btn btn-outline-primary btn-sm rounded-pill px-4 no-print">
                                Continue Shopping
                            </Link>
                        </div>

                        {/* Wishlist Content */}
                        {!wishlistStore.isLoaded ? (
                             <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                             </div>
                        ) : wishlistStore.items.length === 0 ? (
                            <div className="card border-0 shadow-sm rounded-4 p-5 text-center bg-white">
                                <div className="display-1 text-muted mb-4">
                                    <i className="bi bi-heart"></i>
                                </div>
                                <h4 className="fw-bold">Your wishlist is empty</h4>
                                <p className="text-muted mb-4">
                                    Save items you love to find them easily later. 
                                    Start exploring our 2026 collection today.
                                </p>
                                <Link to="/catalog" className="btn btn-primary btn-lg rounded-pill px-5 shadow">
                                    Browse Catalog
                                </Link>
                            </div>
                        ) : (
                            <div className="row g-4">
                                {wishlistStore.items.map((item) => (
                                    <div className="col-md-6 col-xl-4" key={item.id}>
                                        <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden position-relative hover-shadow-transition bg-white">
                                            
                                            {/* Remove Button - Uses the toggle logic to remove */}
                                            <button
                                                className="btn btn-light btn-sm position-absolute top-0 end-0 m-2 rounded-circle shadow-sm border"
                                                style={{
                                                    zIndex: 10,        /* Forces button to stay on top of image */
                                                    width: '35px',
                                                    height: '35px',
                                                    padding: 0,
                                                    display: 'flex',   /* Centers the icon */
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)' /* Ensure it's not transparent */
                                                }}
                                                onClick={(e) => handleRemoveFromWishlist(e, item)}
                                            >
                                                <i className="bi bi-trash3 text-danger">-</i>
                                            </button>

                                            <div className="row g-0 h-100">
                                                {/* Image Section */}
                                                <div className="col-4 position-relative" 
                                                     style={{ cursor: 'pointer' }}
                                                     onClick={() => navigate(`/product/${item.id}`)}>
                                                    <img 
                                                        src={item.mainImageUrl} 
                                                        alt={item.title} 
                                                        className="img-fluid h-100" 
                                                        style={{ objectFit: 'cover', minHeight: '160px' }} 
                                                    />
                                                </div>

                                                {/* Content Section */}
                                                <div className="col-8 d-flex flex-column">
                                                    <div className="card-body p-3 d-flex flex-column">
                                                        <div className="mb-1">
                                                            <span className="badge bg-light text-dark border small fw-normal">
                                                                {item.brand}
                                                            </span>
                                                        </div>
                                                        <h6 className="card-title fw-bold text-truncate mb-1" 
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => navigate(`/product/${item.id}`)}>
                                                            {item.title}
                                                        </h6>
                                                        
                                                        <div className="d-flex align-items-center gap-2 mb-3">
                                                            <span className="text-primary fw-bold">
                                                                Rs. {item.productPrice.toLocaleString()}
                                                            </span>
                                                            {item.productStrikePrice > item.productPrice && (
                                                                <span className="text-muted x-small text-decoration-line-through">
                                                                    Rs. {item.productStrikePrice.toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {/* Quick Add to Cart Integration */}
                                                        <button 
                                                            className="btn btn-dark btn-sm rounded-pill w-100 mt-auto fw-bold py-2 shadow-sm"
                                                            onClick={() => {
                                                                cartStore.addToCart(user?.uid, item as any);
                                                                // Optional: Remove from wishlist after adding to cart
                                                                // wishlistStore.toggleWishlist(user?.uid, item as any);
                                                            }}
                                                        >
                                                            <i className="bi bi-bag-plus me-2"></i> Add to Cart
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
});